import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { tasksAPI, categoriesAPI, placesAPI, usersAPI, customFieldsAPI, inventoryAPI } from '../lib/api';
import { assigneeLabel } from '../lib/publicSite';
import { compressImage } from '../lib/utils';
import { useAuth } from '../context/AuthContext';
import { useBranding } from '../context/BrandingContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { format } from 'date-fns';
import { toast } from 'sonner';
import { 
  ArrowLeft,
  Camera,
  X,
  Plus,
  Calendar as CalendarIcon,
  Loader2,
  MapPin,
  FileText,
  AlertTriangle,
  Milestone,
  Tag,
  UserCheck,
  ClipboardList
} from 'lucide-react';
import Layout from '../components/Layout';

const taskSchema = z.object({
  title: z.string().min(1, 'Title is required'),
  description: z.string().min(1, 'Description is required'),
});

const CreateTaskPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { branding } = useBranding();
  const [loading, setLoading] = useState(false);
  const [deadline, setDeadline] = useState(null);
  const [milestones, setMilestones] = useState([]);
  const [newMilestone, setNewMilestone] = useState('');
  const [images, setImages] = useState([]);
  const [uploadingImage, setUploadingImage] = useState(false);
  
  // Dropdowns data
  const [categories, setCategories] = useState([]);
  const [places, setPlaces] = useState([]);
  const [users, setUsers] = useState([]);
  const [inventoryItems, setInventoryItems] = useState([]);
  
  // Selected values
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedPlace, setSelectedPlace] = useState('');
  const [selectedPriority, setSelectedPriority] = useState('medium');
  const [assignedTo, setAssignedTo] = useState('');
  
  // Custom fields
  const [customFields, setCustomFields] = useState([]);
  const [customFieldValues, setCustomFieldValues] = useState({});
  
  // Parts/Inventory
  const [selectedParts, setSelectedParts] = useState([]);
  const [selectedPartId, setSelectedPartId] = useState('');
  const [partQuantity, setPartQuantity] = useState(1);
  
  // Check if custom fields feature is enabled
  const customFieldsEnabled = branding?.feature_custom_fields !== false;

  const isAdmin = user?.role === 'admin' || user?.role === 'supervisor';

  const { register, handleSubmit, formState: { errors } } = useForm({
    resolver: zodResolver(taskSchema),
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [categoriesRes, placesRes, fieldsRes, inventoryRes] = await Promise.all([
          categoriesAPI.getAll(),
          placesAPI.getAll(),
          customFieldsAPI.getAll(),
          inventoryAPI.getAll()
        ]);
        setCategories(categoriesRes.data);
        setPlaces(placesRes.data);
        setCustomFields(fieldsRes.data);
        setInventoryItems(inventoryRes.data);
        
        if (isAdmin) {
          const usersRes = await usersAPI.getAll();
          setUsers(usersRes.data.filter(u => u.role !== 'admin')); // Can assign to non-admins
        }
      } catch (err) {
        console.error('Failed to fetch data:', err);
      }
    };
    
    fetchData();
  }, [isAdmin]);

  const handleAddPart = () => {
    if (!selectedPartId) return;
    
    const item = inventoryItems.find(i => i.id === selectedPartId);
    if (!item) return;
    
    // Check if already added
    const existing = selectedParts.find(p => p.item_id === selectedPartId);
    if (existing) {
      toast.error('This part is already added');
      return;
    }
    
    // Check availability
    if (item.quantity < partQuantity) {
      toast.error(`Only ${item.quantity} ${item.unit} available`);
      return;
    }
    
    setSelectedParts([...selectedParts, {
      item_id: item.id,
      item_name: item.name,
      quantity: partQuantity,
      cost_per_unit: item.cost_per_unit || 0,
      unit: item.unit
    }]);
    
    setSelectedPartId('');
    setPartQuantity(1);
    toast.success(`${item.name} added`);
  };

  const handleRemovePart = (itemId) => {
    setSelectedParts(selectedParts.filter(p => p.item_id !== itemId));
  };

  const getTotalPartsCost = () => {
    return selectedParts.reduce((total, part) => 
      total + (part.quantity * part.cost_per_unit), 0
    ).toFixed(2);
  };

  const handleAddMilestone = () => {
    if (newMilestone.trim()) {
      setMilestones([...milestones, { title: newMilestone.trim(), description: '' }]);
      setNewMilestone('');
    }
  };

  const handleRemoveMilestone = (index) => {
    setMilestones(milestones.filter((_, i) => i !== index));
  };

  const handleImageUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    setUploadingImage(true);
    
    try {
      const newImages = await Promise.all(
        files.map(async (file) => {
          const compressed = await compressImage(file);
          return {
            id: Math.random().toString(36).substr(2, 9),
            data: compressed,
            caption: file.name
          };
        })
      );
      setImages([...images, ...newImages]);
    } catch (err) {
      toast.error('Failed to process images');
    } finally {
      setUploadingImage(false);
    }
  };

  const handleRemoveImage = (id) => {
    setImages(images.filter(img => img.id !== id));
  };

  const onSubmit = async (data) => {
    setLoading(true);
    
    // Validate required custom fields
    const missingRequired = customFields
      .filter(f => f.required && !customFieldValues[f.id])
      .map(f => f.name);
    
    if (missingRequired.length > 0) {
      toast.error(`Please fill in required fields: ${missingRequired.join(', ')}`);
      setLoading(false);
      return;
    }
    
    try {
      const taskData = {
        ...data,
        place_id: selectedPlace || null,
        category_id: selectedCategory || null,
        priority: selectedPriority,
        deadline: deadline ? deadline.toISOString() : null,
        assigned_to: assignedTo || null,
        milestones,
        before_images: images.map(({ data, caption }) => ({ data, caption, image_type: 'before' })),
        after_images: [],
        custom_field_values: customFieldValues,
      };
      
      const response = await tasksAPI.create(taskData);
      const taskId = response.data.id;
      
      // Add parts to the work order if any selected
      if (selectedParts.length > 0) {
        try {
          for (const part of selectedParts) {
            await tasksAPI.addPart(taskId, {
              item_id: part.item_id,
              quantity: part.quantity
            });
          }
          toast.success(`Work order created with ${selectedParts.length} part(s)`);
        } catch (partErr) {
          console.error('Failed to add parts:', partErr);
          toast.warning('Work order created but some parts could not be added');
        }
      } else {
        toast.success('Work order created successfully');
      }
      
      navigate(`/tasks/${taskId}`);
    } catch (err) {
      toast.error('Failed to create task');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout>
      <div className="max-w-3xl mx-auto space-y-6" data-testid="create-task-page">
        {/* Back Button */}
        <Button 
          variant="ghost" 
          onClick={() => navigate('/tasks')}
          className="btn-ghost flex items-center gap-2 -ml-2"
          data-testid="back-btn"
        >
          <ArrowLeft className="w-5 h-5" />
          Back to Work Orders
        </Button>

        {/* Header */}
        <div>
          <h1 className="font-['Barlow_Condensed'] text-4xl md:text-5xl font-bold uppercase tracking-tight text-white">
            Create Work Order
          </h1>
          <p className="text-slate-400 mt-1">Report a new maintenance issue</p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          {/* Title */}
          <div className="card-industrial">
            <div className="flex items-center gap-2 mb-4">
              <FileText className="w-5 h-5 text-amber-500" />
              <Label className="text-slate-400 uppercase text-xs tracking-wider">
                Work Order Title *
              </Label>
            </div>
            <Input
              {...register('title')}
              placeholder="e.g., Fix broken light in hallway"
              className="input-industrial w-full"
              data-testid="title-input"
            />
            {errors.title && (
              <p className="text-red-400 text-sm mt-2">{errors.title.message}</p>
            )}
          </div>

          {/* Description */}
          <div className="card-industrial">
            <div className="flex items-center gap-2 mb-4">
              <FileText className="w-5 h-5 text-amber-500" />
              <Label className="text-slate-400 uppercase text-xs tracking-wider">
                Description *
              </Label>
            </div>
            <Textarea
              {...register('description')}
              placeholder="Describe the issue in detail..."
              className="input-industrial min-h-[120px] w-full"
              data-testid="description-input"
            />
            {errors.description && (
              <p className="text-red-400 text-sm mt-2">{errors.description.message}</p>
            )}
          </div>

          {/* Category & Priority */}
          <div className="grid md:grid-cols-2 gap-4">
            {/* Category */}
            <div className="card-industrial">
              <div className="flex items-center gap-2 mb-4">
                <Tag className="w-5 h-5 text-amber-500" />
                <Label className="text-slate-400 uppercase text-xs tracking-wider">
                  Category
                </Label>
              </div>
              <Select value={selectedCategory} onValueChange={setSelectedCategory}>
                <SelectTrigger className="bg-slate-950 border-slate-700 text-white w-full h-12" data-testid="category-select">
                  <SelectValue placeholder="Select category" />
                </SelectTrigger>
                <SelectContent className="bg-slate-900 border-slate-700">
                  {categories.map((cat) => (
                    <SelectItem key={cat.id} value={cat.id}>
                      <div className="flex items-center gap-2">
                        <span 
                          className="w-3 h-3 rounded-full" 
                          style={{ backgroundColor: cat.color }}
                        />
                        {cat.name}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Priority */}
            <div className="card-industrial">
              <div className="flex items-center gap-2 mb-4">
                <AlertTriangle className="w-5 h-5 text-amber-500" />
                <Label className="text-slate-400 uppercase text-xs tracking-wider">
                  Priority
                </Label>
              </div>
              <Select value={selectedPriority} onValueChange={setSelectedPriority}>
                <SelectTrigger className="bg-slate-950 border-slate-700 text-white w-full h-12" data-testid="priority-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-slate-900 border-slate-700">
                  <SelectItem value="low">Low</SelectItem>
                  <SelectItem value="medium">Medium</SelectItem>
                  <SelectItem value="high">High</SelectItem>
                  <SelectItem value="critical">Critical</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Location & Deadline */}
          <div className="grid md:grid-cols-2 gap-4">
            {/* Place */}
            <div className="card-industrial">
              <div className="flex items-center gap-2 mb-4">
                <MapPin className="w-5 h-5 text-amber-500" />
                <Label className="text-slate-400 uppercase text-xs tracking-wider">
                  Location
                </Label>
              </div>
              <Select value={selectedPlace} onValueChange={setSelectedPlace}>
                <SelectTrigger className="bg-slate-950 border-slate-700 text-white w-full h-12" data-testid="place-select">
                  <SelectValue placeholder="Select location" />
                </SelectTrigger>
                <SelectContent className="bg-slate-900 border-slate-700 max-h-[280px] overflow-y-auto">
                  {places.map((place) => (
                    <SelectItem key={place.id} value={place.id} className="cursor-pointer">
                      <span className="text-slate-400 text-xs mr-2">{place.building_name} / {place.floor_name}</span>
                      {place.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-slate-400 text-xs mt-3">
                Work orders use a place. Add a floor and a place under Locations before a new building can be selected here.
              </p>
            </div>

            {/* Deadline */}
            <div className="card-industrial">
              <div className="flex items-center gap-2 mb-4">
                <CalendarIcon className="w-5 h-5 text-amber-500" />
                <Label className="text-slate-400 uppercase text-xs tracking-wider">
                  Deadline
                </Label>
              </div>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    type="button"
                    className="w-full h-12 bg-slate-950 border border-slate-700 text-left justify-start px-4 hover:bg-slate-900 hover:border-slate-600"
                    data-testid="deadline-trigger"
                  >
                    <CalendarIcon className="w-4 h-4 mr-3 text-slate-400" />
                    {deadline ? (
                      <span className="text-white">{format(deadline, 'PPP')}</span>
                    ) : (
                      <span className="text-slate-400">Select deadline date</span>
                    )}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0 bg-slate-900 border-slate-700" align="start">
                  <Calendar
                    mode="single"
                    selected={deadline}
                    onSelect={setDeadline}
                    initialFocus
                    className="bg-slate-900"
                    data-testid="deadline-calendar"
                  />
                </PopoverContent>
              </Popover>
              {deadline && (
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => setDeadline(null)}
                  className="mt-2 text-slate-400 hover:text-white text-sm"
                >
                  Clear deadline
                </Button>
              )}
            </div>
          </div>

          {/* Assignment (Admin only) */}
          {isAdmin && (
            <div className="card-industrial">
              <div className="flex items-center gap-2 mb-4">
                <UserCheck className="w-5 h-5 text-amber-500" />
                <Label className="text-slate-400 uppercase text-xs tracking-wider">
                  Assign To
                </Label>
              </div>
              <Select value={assignedTo || "unassigned"} onValueChange={(v) => setAssignedTo(v === "unassigned" ? "" : v)}>
                <SelectTrigger className="bg-slate-950 border-slate-700 text-white w-full h-12" data-testid="assign-select">
                  <SelectValue placeholder="Leave unassigned" />
                </SelectTrigger>
                <SelectContent className="bg-slate-900 border-slate-700">
                  <SelectItem value="unassigned">Unassigned</SelectItem>
                  {users.map((u) => (
                    <SelectItem key={u.id} value={u.id}>
                      <div className="flex items-center gap-2">
                        {assigneeLabel(u, users)}
                        <span className="text-xs text-slate-400 uppercase">{u.role}</span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Milestones */}
          <div className="card-industrial">
            <div className="flex items-center gap-2 mb-4">
              <Milestone className="w-5 h-5 text-amber-500" />
              <Label className="text-slate-400 uppercase text-xs tracking-wider">
                Milestones / Steps
              </Label>
            </div>
            
            {milestones.length > 0 && (
              <div className="space-y-2 mb-4">
                {milestones.map((milestone, index) => (
                  <div 
                    key={index}
                    className="flex items-center gap-3 p-3 bg-slate-700/50 rounded-sm"
                    data-testid={`milestone-item-${index}`}
                  >
                    <span className="text-amber-500 font-mono text-sm">{index + 1}.</span>
                    <span className="flex-1 text-white">{milestone.title}</span>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => handleRemoveMilestone(index)}
                      className="text-slate-400 hover:text-red-400 p-1 h-auto"
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
              </div>
            )}

            <div className="flex gap-2">
              <Input
                value={newMilestone}
                onChange={(e) => setNewMilestone(e.target.value)}
                placeholder="Add a step or milestone..."
                className="input-industrial flex-1"
                onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddMilestone())}
                data-testid="new-milestone-input"
              />
              <Button 
                type="button"
                onClick={handleAddMilestone}
                className="btn-secondary px-4"
                data-testid="add-milestone-btn"
              >
                <Plus className="w-5 h-5" />
              </Button>
            </div>
          </div>

          {/* Custom Fields */}
          {customFieldsEnabled && customFields.length > 0 && (
            <div className="card-industrial">
              <div className="flex items-center gap-2 mb-4">
                <ClipboardList className="w-5 h-5 text-cyan-400" />
                <Label className="text-slate-400 uppercase text-xs tracking-wider">
                  Custom Fields
                </Label>
              </div>
              
              <div className="space-y-4">
                {customFields.map((field) => (
                  <div key={field.id}>
                    <Label className="text-slate-400 text-sm flex items-center gap-2">
                      {field.name}
                      {field.required && <span className="text-amber-400 text-xs">*</span>}
                    </Label>
                    {field.description && (
                      <p className="text-slate-500 text-xs mb-1">{field.description}</p>
                    )}
                    
                    {field.field_type === 'text' && (
                      <Input
                        value={customFieldValues[field.id] || ''}
                        onChange={(e) => setCustomFieldValues(prev => ({ ...prev, [field.id]: e.target.value }))}
                        className="input-industrial mt-1"
                        placeholder={`Enter ${field.name.toLowerCase()}...`}
                        data-testid={`custom-field-${field.id}`}
                      />
                    )}
                    
                    {field.field_type === 'number' && (
                      <Input
                        type="number"
                        value={customFieldValues[field.id] || ''}
                        onChange={(e) => setCustomFieldValues(prev => ({ ...prev, [field.id]: e.target.value }))}
                        className="input-industrial mt-1"
                        placeholder="0"
                        data-testid={`custom-field-${field.id}`}
                      />
                    )}
                    
                    {field.field_type === 'dropdown' && (
                      <Select 
                        value={customFieldValues[field.id] || ''} 
                        onValueChange={(value) => setCustomFieldValues(prev => ({ ...prev, [field.id]: value }))}
                      >
                        <SelectTrigger className="input-industrial mt-1" data-testid={`custom-field-${field.id}`}>
                          <SelectValue placeholder={`Select ${field.name.toLowerCase()}...`} />
                        </SelectTrigger>
                        <SelectContent className="bg-slate-800 border-slate-700">
                          {field.options?.map((option) => (
                            <SelectItem key={option} value={option}>
                              {option}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                    
                    {field.field_type === 'date' && (
                      <Popover>
                        <PopoverTrigger asChild>
                          <Button
                            type="button"
                            className="input-industrial mt-1 w-full justify-start text-left font-normal"
                            data-testid={`custom-field-${field.id}`}
                          >
                            <CalendarIcon className="w-4 h-4 mr-2" />
                            {customFieldValues[field.id] 
                              ? format(new Date(customFieldValues[field.id]), 'PPP')
                              : `Select ${field.name.toLowerCase()}...`}
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0 bg-slate-800 border-slate-700">
                          <Calendar
                            mode="single"
                            selected={customFieldValues[field.id] ? new Date(customFieldValues[field.id]) : undefined}
                            onSelect={(date) => setCustomFieldValues(prev => ({ ...prev, [field.id]: date?.toISOString() }))}
                            className="bg-slate-800"
                          />
                        </PopoverContent>
                      </Popover>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Parts/Inventory (Optional) */}
          <div className="card-industrial">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <Tag className="w-5 h-5 text-green-400" />
                <Label className="text-slate-400 uppercase text-xs tracking-wider">
                  Parts & Materials (Optional)
                </Label>
              </div>
            </div>
            
            <p className="text-slate-400 text-sm mb-4">Add parts that will be needed for this work order</p>

            {/* Add Part Form */}
            <div className="grid md:grid-cols-3 gap-3 mb-4">
              <div className="md:col-span-2">
                <Select value={selectedPartId} onValueChange={setSelectedPartId}>
                  <SelectTrigger className="bg-slate-950 border-slate-700 text-white h-12" data-testid="part-select">
                    <SelectValue placeholder="Select part from inventory..." />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-900 border-slate-700 max-h-[250px] overflow-y-auto">
                    {inventoryItems.filter(item => item.quantity > 0).map((item) => (
                      <SelectItem key={item.id} value={item.id}>
                        <div className="flex justify-between items-center gap-3">
                          <span className="text-white">{item.name}</span>
                          <span className="text-slate-400 text-xs">
                            ({item.quantity} {item.unit} @ ${item.cost_per_unit?.toFixed(2) || '0.00'})
                          </span>
                        </div>
                      </SelectItem>
                    ))}
                    {inventoryItems.filter(item => item.quantity > 0).length === 0 && (
                      <div className="p-3 text-slate-500 text-center text-sm">No parts in stock</div>
                    )}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex gap-2">
                <Input
                  type="number"
                  min="1"
                  value={partQuantity}
                  onChange={(e) => setPartQuantity(parseInt(e.target.value) || 1)}
                  placeholder="Qty"
                  className="input-industrial w-20"
                  data-testid="part-quantity"
                />
                <Button
                  type="button"
                  onClick={handleAddPart}
                  disabled={!selectedPartId}
                  className="btn-primary flex-1"
                  data-testid="add-part-btn"
                >
                  <Plus className="w-4 h-4 mr-1" />
                  Add
                </Button>
              </div>
            </div>

            {/* Selected Parts List */}
            {selectedParts.length > 0 ? (
              <div className="space-y-2">
                {selectedParts.map((part) => (
                  <div 
                    key={part.item_id}
                    className="flex items-center justify-between p-3 bg-slate-800/50 rounded-sm border border-slate-700"
                    data-testid={`selected-part-${part.item_id}`}
                  >
                    <div className="flex items-center gap-3">
                      <Tag className="w-4 h-4 text-green-400" />
                      <div>
                        <p className="text-white font-medium">{part.item_name}</p>
                        <p className="text-slate-400 text-sm">
                          {part.quantity} {part.unit} × ${part.cost_per_unit.toFixed(2)} = ${(part.quantity * part.cost_per_unit).toFixed(2)}
                        </p>
                      </div>
                    </div>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => handleRemovePart(part.item_id)}
                      className="text-red-400 hover:text-red-300 hover:bg-red-500/20 p-2"
                      data-testid={`remove-part-${part.item_id}`}
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
                
                {/* Total Cost */}
                <div className="flex justify-between items-center pt-3 mt-2 border-t border-slate-700">
                  <span className="text-slate-400 text-sm uppercase tracking-wide">Estimated Parts Cost</span>
                  <span className="text-green-400 font-bold text-lg">${getTotalPartsCost()}</span>
                </div>
              </div>
            ) : (
              <p className="text-slate-500 text-center py-3 text-sm">No parts added yet</p>
            )}
          </div>

          {/* Before Photos */}
          <div className="card-industrial">
            <div className="flex items-center gap-2 mb-4">
              <Camera className="w-5 h-5 text-blue-400" />
              <Label className="text-slate-400 uppercase text-xs tracking-wider">
                Before Photos
              </Label>
            </div>
            <p className="text-slate-400 text-sm mb-4">Document the problem (you can add "After" photos once the work is done)</p>

            {images.length > 0 && (
              <div className="image-grid mb-4">
                {images.map((image) => (
                  <div key={image.id} className="image-grid-item relative group border border-blue-500/30">
                    <img src={image.data} alt={image.caption} />
                    <span className="absolute top-1 left-1 bg-blue-500/80 text-white text-xs px-2 py-0.5 rounded-sm">BEFORE</span>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => handleRemoveImage(image.id)}
                      className="absolute top-1 right-1 bg-black/50 hover:bg-red-500/80 text-white p-1 h-auto opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
              </div>
            )}

            <label className="image-upload-area p-6 text-center cursor-pointer block border-blue-500/30 hover:border-blue-500/50" data-testid="image-upload-area">
              <input
                type="file"
                accept="image/*"
                multiple
                onChange={handleImageUpload}
                className="hidden"
                data-testid="image-input"
              />
              {uploadingImage ? (
                <Loader2 className="w-8 h-8 animate-spin text-blue-400 mx-auto mb-2" />
              ) : (
                <Camera className="w-8 h-8 text-blue-400 mx-auto mb-2" />
              )}
              <p className="text-slate-400">
                {uploadingImage ? 'Processing...' : 'Click to upload before photos'}
              </p>
              <p className="text-slate-500 text-sm mt-1">Show the problem to document</p>
            </label>
          </div>

          {/* Submit */}
          <div className="flex gap-4">
            <Button
              type="button"
              onClick={() => navigate('/tasks')}
              className="btn-secondary flex-1"
              data-testid="cancel-btn"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={loading}
              className="btn-primary flex-1 flex items-center justify-center gap-2"
              data-testid="submit-btn"
            >
              {loading ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Creating...
                </>
              ) : (
                'Create Work Order'
              )}
            </Button>
          </div>
        </form>
      </div>
    </Layout>
  );
};

export default CreateTaskPage;
